<template>
  <div :class="['apInputMask', {'apInputMask--error': hasErrorText}]">
    <div class="apInputMask__inner">
      <label
        v-if="label"
        class="apInputMask__label"
      >
        {{ label }}
      </label>
      <div :class="['apInputMask__field', {'apInputMask__field--border': border}]">
        <input
          v-mask="composeMask"
          class="apInputMask__input"
          :value="modelValue"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="input"
        >
        <icon-wrapper
          v-if="currentIcon"
          :icon-name="currentIcon"
        />
      </div>
      <div
        v-if="hintOrError"
        class="apInputMask__hint"
      >
        {{ hintOrError }}
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { mask } from 'vue-the-mask';
import { useI18n } from 'vue-i18n';

import IconWrapper from '@/components/icons/iconWrapper.vue';

export default defineComponent({
  name: 'ApInputMask',

  directives: { mask },

  components: {
    IconWrapper
  },
  
  props: {
    type: {
      type: String,
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    iconName: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    errorText: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: ''
    },
    mask: {
      type: String,
      default: '',
    },
    border: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: 256
    }
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const currentIcon = ref('');

    const hasErrorText = computed(() => props.errorText.length > 0);
    
    const hintOrError = computed(() => {      
      if (hasErrorText.value){
        return props.errorText;
      }
      return props.hint;
    });

    const input = (e: Event) : void => {
      const text = e.target as HTMLInputElement;
      const value = text.value.replace(/\s+/g, '');
      if (props.type === 'cardNumber') {
        const cardRegex =  {
          visa: /^(?:4[0-9]{12}(?:[0-9]{3})?)/,
          mastercard: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
          mir: /^220[0-4][0-9]/,
          humo: /^9860[0-9]/,
          uzcard: /^8600[0-9]/,
          elkard: /^9417[0-9]/
        };

        if (value.length) {
          for (const key in cardRegex) {
            if (cardRegex[key].test(value) ) {
              currentIcon.value = key;
            }      
          }
        } else {
          currentIcon.value = '';
        }
      }

      emit('update:modelValue', value);
    };

    const composeMask = computed(() => {
      switch (props.type){
      case 'otp':
        return '### ###';
      case 'cardNumber':
        return '#### #### #### ####';
      case 'expireDate':
        return '##/##';
      case 'cvv': 
        return '###';
      case 'amount':
        return '#######';
      case 'sum': 
        return '#'.repeat(props.max);
      default: 
        return '#'.repeat(25);
      }
    });

    const composePlaceholder = computed(() => {
      switch (props.type){
      case 'cardNumber':
        return t('card_number');
      case 'expireDate':
        return 'MM/YY';
      case 'cvv': 
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        currentIcon.value = 'cvv';
        return 'CVV/CVС';
      default: 
        return '';
      }
    });


    
    return {
      currentIcon,
      hasErrorText,
      hintOrError,
      composeMask,
      placeholder:composePlaceholder,
      input,
    };
  },
});
</script>

<style lang="scss" scoped>
.apInputMask{
  display: flex;
  flex-direction: column;
  &__input{
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    border: none;
    &:focus{
      outline: none;
    }
    &:disabled{
      background-color: #ffffff;
    }
  }
  &__hint {
      font-size: 12px;
      margin-top: 6px;
      color: rgba(0,0,0, .6);
  }
  &__field{
    display: flex;
    overflow: hidden;
    padding: 10px;
    align-items: center;
    width: 100%;
    position: relative;
    &--border{
      border: none;
    }
  }
  &__label{
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    color: rgba(0, 0, 0, 0.72);
    margin-bottom: 6px;
    display: block;
  }
  &__icon{
    display: block;
    height: 24px;
    width: 24px;
  }
}
.default{
  .apInputMask{
    &__field{
      border: 1px solid $secondary-gray;
      border-radius: 6px;
      line-height: 22px;
      &--border{
        border: none;
      }
    }
  }
  .apInputMask--error{
    .apInputMask{
      &__field{
        border: 1px solid red;
      }
      &__label{
        color: red;
      }
      &__hint{
        color: red;
      }
      &__code{
        color: red;
      }
      &__input{
        color: red;
      }
    }
  }
}
.tele2{
  .apInputMask{
    font-family: "Open Sans";      
    &__field{
      border-bottom: 1px solid $tele2-primary-gray;
      line-height: 24px;
      padding: 8px 0;
      line-height: 24px;
      &--border{
        border: none;
        padding: 8px;
      }
    }
    &__label{
      font-weight: 600;
      color: $tele2-primary-gray;
      font-size: 13px;
      line-height: 16px;
      margin-bottom: 7px;
    }
  }
  .apInputMask--error{
    .apInputMask{
      &__field{
        border-bottom-color: 1px solid red;
      }
      &__label{
        color: red;
      }
      &__hint{
        color: red;
      }
      &__code{
        color: red;
      }
      &__input{
        color: red;
      }
    }
  }
}
.altel{
  .apInputMask{
    &__field{
      border: 1px solid $secondary-gray;
      background: #F8F8F8;
      line-height: 24px;
      border-radius: 4px;
      &--border{
        border: none;
        background:none;
        .apInputMask__input{
          background:none;
        }
      }
    }
    &__input{
      background: #F8F8F8;
    }
    &__label{
      font-weight: 500;
      color: $tele2-primary-gray;
      font-size: 18px;
      line-height: 22px;
      margin-bottom: 8px;
    }
  }
  .apInputMask--error{
  .apInputMask{
    &__field{
      border-color: red;
    }
    &__label{
      color: red;
    }
    &__hint{
      color: red;
    }
    &__code{
      color: red;
    }
    &__input{
      color: red;
    }
  }
}
}
</style>
